/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnInit, signal, SimpleChanges} from '@angular/core';
import {deferSignalViewWrite, notifySignalView} from '../../common/util/template-state';
import * as _ from 'lodash';

import template from './named-location-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {normalizeTargetingSearchPage} from '../normalize-targeting-search-page';
import {TargetingTemplateHook} from './targeting-template.component';
import {TargetingHelper} from '../targeting-helper';
import {getCountryEntry} from '../../common/directives/country-select/countries';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocationApiService} from '../../trigger/location-api.service';

const NORWAY = {code: 'NO', name: 'Norway'};
const SWEDEN = {code: 'SE', name: 'Sweden'};
const DENMARK = {code: 'DK', name: 'Denmark'};
const POLITICAL_GEO_GUARD_COUNTRIES = ['FI', 'SE'];

@Component({
  standalone: false,
  selector: 'adn-named-location-targeting',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NamedLocationTargetingComponent implements OnInit, OnChanges {
  @Input() model: any;
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.namedLocation;
  @Input() underModel: any;
  @Input() modelLineItem: any;
  @Input() election: any;
  @Input() lineItemType: string;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  blockPostcodes = false;
  politicalGeoGuardActive = false;
  politicalGeoAckRequired = false;
  politicalGeoAck = {value: false};
  allowedLocationTargetingTypes: string[] | null = null;
  showOptions: any[] = [];
  restriction = {value: ''};
  search = {term: ''};
  municipalitySearch = {term: ''};
  filterBucket: string;
  aggregations: any;
  list: any;
  searchResults: any;
  /** Snapshot — search replace of items mid-CD is NG0100. */
  displaySearchResults: any;
  treeResults: any;
  treeChildren: any = {};
  /** Snapshot — browse tree replace of items mid-CD is NG0100. */
  displayTreeResults: any;
  displayTreeChildren: any = {};
  municipalities: any[] = [];
  showMunicipalityTargeting = false;
  readonly viewRevision = signal(0);

  searchMeta: any = {
    pageSize: 50,
    currentPage: 1,
    resolved: true,
    changePage: () => this.doSearch(false)
  };

  treeMeta: any = {
    pageSize: 200,
    currentPage: 1,
    resolved: true
  };

  private municipalitiesByCountry: any = {};

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      const summaryData = TargetingHelper.getSummary(this.model, ADN_TARGETING_TYPES.namedLocation);
      return {
        vm: undefined,
        model: _.cloneDeep(this.model),
        constant: ADN_TARGETING_TYPES.namedLocation,
        summary: (this.model && this.model.negated ? 'NOT: ' : '') + summaryData
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  constructor(
    private readonly locationApi: LocationApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model) {
      this.showMunicipalityTargeting = this.showMunicipalityTargeting ||
        this.hasMunicipalityTargeting(changes.model.currentValue);
      this.ensureModel();
      if (!changes.model.firstChange) {
        this.markMunicipalitySelections();
      }
    }
  }

  ngOnInit(): void {
    this.showMunicipalityTargeting = this.showMunicipalityTargeting || this.hasMunicipalityTargeting(this.model);
    this.ensureModel();
    this.blockPostcodes = this.lineItemType === 'POLITICAL';
    this.politicalGeoAck = {value: (this.model?.locations || []).length > 0};
    this.initShowOptions();
    this.initPoliticalGuard();
    this.initElectionLocationTargeting();
    this.loadMunicipalities(this.restriction.value);

    if (!this.tabController) {
      return;
    }

    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.namedLocation.widget, () => this.loadTreeRoots(), copyTemplate);
    this.tabController.callBeforeSubmit(() => this.prepareForSave());
  }

  isNamedLocationGeoAddDisabled(location?: any): boolean {
    if (location && this.allowedLocationTargetingTypes &&
      this.allowedLocationTargetingTypes.indexOf(location.type) === -1) {
      return true;
    }
    return !!(this.politicalGeoAckRequired && !this.politicalGeoAck.value);
  }

  allowedLocationTargetingTypeLabels(): string {
    const labels = _.map(this.allowedLocationTargetingTypes || [], (type) => this.columnLabelForType(type));
    if (labels.length < 2) {
      return labels[0] || '';
    }
    return labels.slice(0, -1).join(', ') + (labels.length > 2 ? ', and ' : ' and ') + labels[labels.length - 1];
  }

  onSearchKeypress(event: KeyboardEvent): void {
    if (event && event.key === 'Enter') {
      event.preventDefault();
      this.runSearch();
    }
  }

  runSearch(): void {
    this.filterBucket = undefined;
    this.doSearch(true);
  }

  clearSearch(): void {
    this.search.term = '';
    this.filterBucket = undefined;
    this.searchResults = undefined;
    this.displaySearchResults = undefined;
    this.aggregations = undefined;
  }

  filterOnBucket(bucketName?: string): void {
    this.filterBucket = bucketName;
    this.doSearch(true);
  }

  changeSearchPage(page: number): void {
    if (page < 1 || !this.searchResults) {
      return;
    }
    if (this.searchMeta.pageSize * (page - 1) >= this.searchResults.totalHits && page > 1) {
      return;
    }
    this.searchMeta.currentPage = page;
    this.doSearch(false);
  }

  setRestriction(code: string): void {
    this.restriction.value = code || '';
    this.loadMunicipalities(this.restriction.value);
  }

  loadMunicipalitiesOnRestriction(): void {
    this.loadMunicipalities(this.restriction.value);
  }

  addToTarget(location: any): void {
    if (this.isNamedLocationGeoAddDisabled(location)) {
      return;
    }
    this.ensureModel();
    const alreadyTargeted = _.find(this.model.locations, (targetedL: any) => targetedL.id === location.id);
    if (!alreadyTargeted) {
      this.model.locations.push(location);
    }
    location.isSelected = true;
  }

  removeFromTarget(location: any): void {
    this.model.locations = _.filter(this.model.locations, (l: any) => l.id !== location.id);
    this.clearSelectedFlag(location.id);
    location.isSelected = false;
  }

  removeAllFromTarget(): void {
    this.model.locations = [];
    _.forEach(_.get(this, ['treeResults', 'items']) || [], (item: any) => {
      item.isSelected = false;
    });
    _.forEach(this.treeChildren || {}, (treeChild: any) => {
      _.forEach(_.get(treeChild, ['items']) || [], (subItem: any) => {
        subItem.isSelected = false;
      });
    });
    _.forEach(_.get(this, ['displaySearchResults', 'items']) || _.get(this, ['searchResults', 'items']) || [], (item: any) => {
      item.isSelected = false;
    });
  }

  addMunicipalityToTarget(municipality: any): void {
    if (!this.showMunicipalityTargeting || this.isNamedLocationGeoAddDisabled()) {
      return;
    }
    this.ensureModel();
    if (!this.isMunicipalityTargeted(municipality.id)) {
      this.model.municipalities.push({id: municipality.id, name: municipality.name});
    }
    municipality.isSelected = true;
  }

  removeMunicipalityFromTarget(municipality: any): void {
    this.model.municipalities = _.filter(this.model.municipalities, (entry: any) => {
      return this.municipalityId(entry) !== municipality.id;
    });
    municipality.isSelected = false;
  }

  filteredMunicipalities(): any[] {
    const term = (this.municipalitySearch.term || '').toLowerCase();
    const municipalities = this.municipalities || [];
    if (!term) {
      return municipalities;
    }
    return _.filter(municipalities, (m: any) => {
      return (m.name || '').toLowerCase().indexOf(term) > -1 ||
        (m.region || '').toLowerCase().indexOf(term) > -1;
    });
  }

  selectedMunicipalityRows(): any[] {
    return _.map(this.model?.municipalities || [], (entry: any) => {
      const id = this.municipalityId(entry);
      return _.find(this.municipalities, (municipality: any) => municipality.id === id) ||
        (_.isObject(entry) ? entry : {id, name: id});
    });
  }

  childKey(result: any): string {
    return result.id + '-' + (result.currentPage || 1);
  }

  childrenOf(result: any): any {
    return this.displayTreeChildren[this.childKey(result)];
  }

  toggleChildren(result: any): void {
    result.showChildren = !result.showChildren;
    this.getChildren(result);
  }

  changeTreePage(result: any, page: number): void {
    const child = this.childrenOf(result);
    if (!child || page < 1) {
      return;
    }
    if (this.treeMeta.pageSize * (page - 1) >= child.totalHits && page > 1) {
      return;
    }
    result.currentPage = page;
    // Force reload for the new page key.
    this.getChildren(result, true);
  }

  rowCanExpand(node: any): boolean {
    if (!node || !node.type) {
      return true;
    }
    if (node.type === 'POSTCODE' || node.type === 'POSTAL_CODE') {
      return false;
    }
    if (this.blockPostcodes && node.type === 'CITY') {
      return false;
    }
    return true;
  }

  listShowsExpander(childPage: any): boolean {
    const type = _.get(childPage, ['type'], '');
    if (type === 'POSTCODE' || type === 'POSTAL_CODE') {
      return false;
    }
    if (this.blockPostcodes && type === 'CITY') {
      return false;
    }
    return true;
  }

  columnLabelForType(type: string): string {
    switch (type) {
      case 'COUNTRY':
        return 'Country';
      case 'REGION':
      case 'STATE':
        return 'Region';
      case 'DISTRICT':
        return 'District';
      case 'CITY':
        return 'City';
      case 'POSTCODE':
      case 'POSTAL_CODE':
        return 'Postcode';
      default:
        return type || 'Location';
    }
  }

  pageWindow(result: any): {start: number; end: number; total: number} {
    const child = this.childrenOf(result) || {totalHits: 0};
    const total = child.totalHits || 0;
    const page = result.currentPage || 1;
    const start = total === 0 ? 0 : this.treeMeta.pageSize * (page - 1) + 1;
    const end = Math.min(this.treeMeta.pageSize * page, total);
    return {start, end, total};
  }

  private ensureModel(): void {
    if (!this.model) {
      return;
    }
    this.model.locations = this.model.locations || [];
    this.model.municipalities = this.model.municipalities || [];
    this.model.negated = this.model.negated || false;
  }

  private initPoliticalGuard(): void {
    const siteCountries = _.get(this.modelLineItem, ['targeting', ADN_TARGETING_TYPES.siteCountry.targets, ADN_TARGETING_TYPES.siteCountry.apiProp]) || [];
    this.politicalGeoGuardActive =
      this.LocalNetworkProfile.isMarketplacePlatform() &&
      this.blockPostcodes &&
      (siteCountries.length === 0 || _.some(siteCountries, (country) => POLITICAL_GEO_GUARD_COUNTRIES.indexOf(country) > -1));
  }

  private initElectionLocationTargeting(): void {
    const allowedTypes = _.get(this.election, 'allowedLocationTargetingTypes');
    const usesElectionLocationRules = _.get(this.election, 'type') !== 'OTHER' || !!_.get(this.election, 'jurisdictionType');
    this.allowedLocationTargetingTypes = this.lineItemType === 'POLITICAL' && usesElectionLocationRules && _.isArray(allowedTypes)
      ? allowedTypes.slice()
      : null;
    this.politicalGeoAckRequired = this.politicalGeoGuardActive && this.allowedLocationTargetingTypes === null;
  }

  private initShowOptions(): void {
    this.showOptions = [];
    const lineItemId = _.get(this.modelLineItem, ['id'], undefined);
    const siteCountries = _.get(this.modelLineItem, ['targeting', ADN_TARGETING_TYPES.siteCountry.targets, ADN_TARGETING_TYPES.siteCountry.apiProp]) || [];

    if (lineItemId) {
      const productName = _.get(this.modelLineItem, ['axProduct', 'name']) || '';
      if (productName.indexOf('Norway') > -1 || productName.indexOf('Norge') > -1) {
        this.showOptions.push(NORWAY);
      }
      if (productName.indexOf('Sweden') > -1 || productName.indexOf('Sverige') > -1) {
        this.showOptions.push(SWEDEN);
      }
      if (productName.indexOf('Denmark') > -1 || productName.indexOf('Danmark') > -1) {
        this.showOptions.push(DENMARK);
      }
    }
    if (!this.showOptions.length && siteCountries.length > 0) {
      if (siteCountries.indexOf('NO') > -1) {
        this.showOptions.push(NORWAY);
      }
      if (siteCountries.indexOf('SE') > -1) {
        this.showOptions.push(SWEDEN);
      }
      if (siteCountries.indexOf('DK') > -1) {
        this.showOptions.push(DENMARK);
      }
    }
    const defaults = this.LocalNetworkProfile.getDefaults();
    if (_.get(defaults, ['namedLocations'], []).length) {
      _.forEach(defaults.namedLocations, (loc: string) => {
        const countryEntry = getCountryEntry(loc);
        if (countryEntry) {
          this.showOptions.push(countryEntry);
        }
      });
    }
    if (this.showOptions.length > 0) {
      this.restriction.value = this.showOptions[0].code;
    }
  }

  private municipalityId(entry: any): any {
    return _.isObject(entry) ? (entry as any).id : entry;
  }

  private hasMunicipalityTargeting(model: any): boolean {
    return _.get(model, ['municipalities'], []).length > 0;
  }

  private isMunicipalityTargeted(id: any): boolean {
    return _.some(this.model?.municipalities, (entry: any) => this.municipalityId(entry) === id);
  }

  private sortMunicipalities(municipalities: any[]): any[] {
    return (municipalities || []).slice().sort((a: any, b: any) => {
      return (a.name || '').localeCompare(b.name || '', 'sv');
    });
  }

  private markMunicipalitySelections(): void {
    _.forEach(this.municipalities, (municipality: any) => {
      municipality.isSelected = this.isMunicipalityTargeted(municipality.id);
    });
  }

  private loadMunicipalities(countryCode: string): void {
    this.municipalitySearch.term = '';
    if (!this.showMunicipalityTargeting || !countryCode) {
      this.municipalities = [];
      return;
    }
    if (this.municipalitiesByCountry[countryCode]) {
      this.municipalities = this.municipalitiesByCountry[countryCode];
      this.markMunicipalitySelections();
      return;
    }
    this.municipalities = [];
    this.locationApi.getMunicipalities(countryCode).subscribe({
      next: (municipalities) => {
        const sorted = this.sortMunicipalities(municipalities);
        this.municipalitiesByCountry[countryCode] = sorted;
        if (this.restriction.value === countryCode) {
          this.municipalities = sorted;
          this.markMunicipalitySelections();
        }
        notifySignalView(this.viewRevision);
      },
      error: () => {
        this.municipalitiesByCountry[countryCode] = [];
        notifySignalView(this.viewRevision);
      }
    });
  }

  private doSearch(newSearch: boolean): void {
    this.searchMeta.resolved = false;
    if (newSearch) {
      this.searchMeta.currentPage = 1;
    }
    if (this.allowedLocationTargetingTypes && this.allowedLocationTargetingTypes.length === 0) {
      const emptyPage = this.normalizeLocationPage({searchResults: [], aggregations: {}}, this.searchMeta.pageSize, 1);
      this.aggregations = {};
      this.list = emptyPage;
      this.searchResults = this.toPagedItems(emptyPage);
      this.displaySearchResults = this.toPagedItems(emptyPage);
      this.searchMeta.resolved = true;
      return;
    }
    const queryParams: any = {
      q: this.search.term || '',
      types: this.filterBucket || (this.allowedLocationTargetingTypes?.length ? this.allowedLocationTargetingTypes : undefined),
      pageSize: this.searchMeta.pageSize,
      page: this.searchMeta.currentPage - 1,
      notTypes: this.blockPostcodes ? 'POSTCODE' : ''
    };
    if (this.restriction.value) {
      queryParams.countryId = this.restriction.value;
    }
    this.locationApi.search(queryParams).subscribe((page) => {
      const normalizedPage = this.normalizeLocationPage(page, this.searchMeta.pageSize, this.searchMeta.currentPage);
      const aggregations = _.pick(normalizedPage.aggregations, 'types');
      const searchResults = this.toPagedItems(normalizedPage);
      _.forEach(searchResults.items, (l: any) => {
        l.isSelected = !!_.find(this.model.locations, (targetedL: any) => l.id === targetedL.id);
      });
      this.aggregations = aggregations;
      this.list = normalizedPage;
      this.searchResults = searchResults;
      this.searchMeta.resolved = true;
      deferSignalViewWrite(this.viewRevision, () => {
        this.displaySearchResults = {
          items: (searchResults.items || []).slice(),
          totalHits: searchResults.totalHits,
          pageStart: searchResults.pageStart,
          pageEnd: searchResults.pageEnd
        };
      });
    });
  }

  private loadTreeRoots(): void {
    this.locationApi.search({
      q: '',
      types: 'CONTINENT',
      blankOrderBy: true,
      pageSize: this.treeMeta.pageSize,
      page: this.treeMeta.currentPage - 1
    }).subscribe((page) => {
      const treeResults = this.toPagedItems(
        this.normalizeLocationPage(page, this.treeMeta.pageSize, this.treeMeta.currentPage)
      );
      _.forEach(treeResults.items, (l: any) => {
        l.isSelected = !!_.find(this.model.locations, (targetedL: any) => l.id === targetedL.id);
      });
      this.treeResults = treeResults;
      this.treeMeta.resolved = true;
      deferSignalViewWrite(this.viewRevision, () => {
        this.displayTreeResults = {
          items: (treeResults.items || []).slice(),
          totalHits: treeResults.totalHits,
          pageStart: treeResults.pageStart,
          pageEnd: treeResults.pageEnd
        };
      });
    });
  }

  private getChildren(result: any, force?: boolean): void {
    result.currentPage = result.currentPage || 1;
    const childId = this.childKey(result);
    if (!result.showChildren || (!force && _.get(this.treeChildren[childId], 'items'))) {
      return;
    }
    this.treeChildren[childId] = this.treeChildren[childId] || {};
    this.treeChildren[childId].resolved = false;
    this.snapshotTreeChild(childId);
    this.locationApi.search({
      parent: result.id,
      pageSize: this.treeMeta.pageSize,
      blankOrderBy: true,
      page: result.currentPage - 1
    }).subscribe((page) => {
      const normalizedPage = this.normalizeLocationPage(page, this.treeMeta.pageSize, result.currentPage);
      const children = Object.assign(this.toPagedItems(normalizedPage), {
        type: _.get(normalizedPage, ['searchResults', 0, 'type'], ''),
        resolved: true,
        pageResolved: true
      });
      _.forEach(children.items, (l: any) => {
        l.context = result.name + (result.context ? (', ' + result.context) : '');
        l.isSelected = !!_.find(this.model.locations, (targetedL: any) => l.id === targetedL.id);
      });
      this.treeChildren[childId] = children;
      this.snapshotTreeChild(childId);
    });
  }

  private snapshotTreeChild(childId: string): void {
    deferSignalViewWrite(this.viewRevision, () => {
      const live = this.treeChildren[childId];
      if (!live) {
        const next = Object.assign({}, this.displayTreeChildren);
        delete next[childId];
        this.displayTreeChildren = next;
        return;
      }
      this.displayTreeChildren = Object.assign({}, this.displayTreeChildren, {
        [childId]: Object.assign({}, live, {
          items: (live.items || []).slice()
        })
      });
    });
  }

  private clearSelectedFlag(locationId: any): void {
    _.forEach(_.get(this, ['treeResults', 'items']) || [], (item: any) => {
      if (item.id === locationId) {
        item.isSelected = false;
      }
    });
    _.forEach(this.treeChildren || {}, (treeChild: any) => {
      _.forEach(_.get(treeChild, ['items']) || [], (subItem: any) => {
        if (subItem.id === locationId) {
          subItem.isSelected = false;
        }
      });
    });
    _.forEach(_.get(this, ['displaySearchResults', 'items']) || _.get(this, ['searchResults', 'items']) || [], (item: any) => {
      if (item.id === locationId) {
        item.isSelected = false;
      }
    });
  }

  private prepareForSave(): void {
    _.forEach(this.model?.locations, (l: any) => {
      delete l.isSelected;
    });
  }

  private copyTemplate(tpl: any): void {
    if (!this.model) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || {locations: [], municipalities: []};
    if (!this.showMunicipalityTargeting) {
      copied.municipalities = [];
    }
    Object.keys(this.model).forEach((key) => delete this.model[key]);
    Object.assign(this.model, copied);
    this.ensureModel();
    this.politicalGeoAck.value = (this.model.locations || []).length > 0;
    this.markMunicipalitySelections();
    this.clearSearch();
    const searchItems = this.displaySearchResults?.items || this.searchResults?.items;
    if (searchItems) {
      _.forEach(searchItems, (l: any) => {
        l.isSelected = !!_.find(this.model.locations, (targetedL: any) => l.id === targetedL.id);
      });
    }
    if (this.treeResults && this.treeResults.items) {
      _.forEach(this.treeResults.items, (l: any) => {
        l.isSelected = !!_.find(this.model.locations, (targetedL: any) => l.id === targetedL.id);
      });
    }
  }

  private normalizeLocationPage(page: any, pageSize: number, currentPage: number): any {
    return normalizeTargetingSearchPage(page, {pageSize, currentPage});
  }

  private toPagedItems(page: any): any {
    return {
      items: page.searchResults,
      totalHits: page.totalHits,
      pageStart: page.pageStart,
      pageEnd: page.pageEnd
    };
  }
}
